/* Importing the necessary libraries and files */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form, FormikProvider, useFormik } from 'formik';
import moment from 'moment';
import PropTypes from 'prop-types';

/* Importing the components from the material-ui library. */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { DateTimePicker } from '@mui/x-date-pickers';

/* Importing components from the components folder. */
import AutoCompleteLocation from '../../components/AutoCompleteLocation';
import AutoCompletePatient from '../../components/AutoCompletePatient';
import FormHelpDialog from '../../components/FormHelpDialog';

/* Importing the functions from the Redux slices. */
import { getEncounter, postEncounter, putEncounter } from '../../features/encounter/encounterSlice';

/* Importing the functions from the utils folder. */
import fetchAndHandleData from '../../utils/fetchAndHandleData';
import getInitialValues from '../../utils/getInitialValues';
import handleFormResponse from '../../utils/handleFormResponse';
import { fEncounter } from '../../utils/Encounter/Encounter.utils.format';
import { mEncounter } from '../../utils/Encounter/Encounter.utils.map';

/* Importing constants from Encounter.ValueSets.js file. */
import { ENCOUNTER_STATUS, V3_ACT_ENCOUNTER_CODE } from '../../utils/Encounter/Encounter.utils.ValueSets';

/* Defining the propTypes for the EncounterAdd component. */
EncounterAdd.propTypes = {
  isEdit: PropTypes.bool,
  patientID: PropTypes.string,
  isAddOpen: PropTypes.bool,
  encounterID: PropTypes.string,
  setIsAddOpen: PropTypes.func,
};

/**
 * The component that renders the encounter add page.
 * It contains a form that allows the user to add a new encounter.
 */
export default function EncounterAdd(props) {
  const { isEdit, isAddOpen, encounterID, patientID, setIsAddOpen } = props;

  const dispatch = useDispatch();

  const { Encounters } = useSelector((state) => state.encounter);
  const currentEncounter = Encounters.find((encounter) => encounter.id === encounterID);

  const [title, setTitle] = useState('Add Encounter');
  const [patientOptionsDisabled, setPatientOptionsDisabled] = useState(false);
  const [pageEncounter, setPageEncounter] = useState({});
  const [formHelpOpen, setFormHelpOpen] = useState(false);
  const [valueSet, setValueSet] = useState(null);

  const handleFormHelpOpen = (valueSet) => {
    setValueSet(valueSet);
    setFormHelpOpen(true);
  };

  const handleClose = useCallback(() => {
    setIsAddOpen(false);
  }, [setIsAddOpen]);

  const defaultValues = useMemo(
    () => ({
      id: '',
      status: isEdit ? '' : 'in-progress',
      class: '',
      subject: patientID || '',
      period_start: isEdit ? '' : moment().format('YYYY-MM-DD[T]HH:mm:ssZ'),
      period_end: '',
      diagnosis_condition: '',
      location: '',
    }),
    [isEdit, patientID]
  );

  /* Using the useFormik hook to create a formik object. */
  const formik = useFormik({
    initialValues: getInitialValues({
      defaultValues,
      data: pageEncounter,
    }),
    onSubmit: (values) => {
      const encounterData = mEncounter(values);
      if (isEdit) {
        submitPutEncounter(encounterData);
      } else {
        submitPostEncounter(encounterData);
      }
    },
  });

  const submitPostEncounter = async (values) => {
    const resultAction = await dispatch(postEncounter(values));
    const result = await handleFormResponse({
      resultAction,
      successTitle: 'Encounter added successfully!',
      navigatePath: '',
      navigateOnSuccess: false,
      closeDialog: true,
      handleClose,
    });
    if (result) {
      resetForm();
    }
    setSubmitting(false);
  };

  const submitPutEncounter = async (values) => {
    const data = { encounterID: values.id, data: values };
    const resultAction = await dispatch(putEncounter(data));
    const result = await handleFormResponse({
      resultAction,
      successTitle: 'Encounter updated successfully!',
      navigatePath: '',
      navigateOnSuccess: false,
      closeDialog: true,
      handleClose,
    });

    if (result) {
      resetForm();
    }

    setSubmitting(false);
  };

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
    setSubmitting,
    setValues,
    resetForm,
    values,
  } = formik;

  useEffect(() => {
    if (patientID) {
      setPatientOptionsDisabled(true);
    }
  }, [patientID, setFieldValue]);

  useEffect(() => {
    if (isEdit) {
      setTitle(encounterID ? `Edit Encounter - ${encounterID}` : 'Edit Device');
    } else {
      setTitle('Add Encounter');
    }
  }, [encounterID, isEdit]);

  useEffect(() => {
    if (!currentEncounter) return;
    const encounterObj = fEncounter(currentEncounter);
    setPageEncounter(encounterObj);
    setValues(getInitialValues({ data: encounterObj, defaultValues }));
  }, [currentEncounter, defaultValues, setValues]);

  const initializeData = useCallback(async () => {
    if (isEdit && encounterID) {
      const hasEncounter = await fetchAndHandleData({
        dispatch,
        fetchAction: getEncounter,
        id: encounterID,
        errorMessage: 'No Encounter record found',
        navigateOnFailure: false,
      });
      if (!hasEncounter) {
        handleClose();
      }
    } else {
      resetForm();
    }
  }, [dispatch, encounterID, handleClose, isEdit, resetForm]);

  /* Calling the initializeData function when the component mounts and when the isEdit state changes. */
  useEffect(() => {
    initializeData();
  }, [initializeData]);

  return (
    <>
      <Dialog open={isAddOpen} onClose={handleClose}>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
              <Grid container item xs={12} columnSpacing={4} rowSpacing={3}>
                <Grid item xs={12} md={12}>
                  <Stack spacing={3}>
                    <Stack spacing={2}>
                      <Typography variant="subtitle1" sx={{ pt: 1 }}>
                        Details
                      </Typography>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        fullWidth
                        select
                        label="Class"
                        {...getFieldProps('class')}
                        error={Boolean(touched.class && errors.class)}
                        helperText={touched.class && errors.class}
                      >
                        {V3_ACT_ENCOUNTER_CODE.values.map((el) => (
                          <MenuItem key={el.code} value={el.code}>
                            {el.display}
                          </MenuItem>
                        ))}
                      </TextField>
                      <IconButton
                        aria-label="help"
                        onClick={() => {
                          handleFormHelpOpen(V3_ACT_ENCOUNTER_CODE);
                        }}
                      >
                        <HelpOutlineIcon />
                      </IconButton>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        fullWidth
                        select
                        label="Status"
                        {...getFieldProps('status')}
                        error={Boolean(touched.status && errors.status)}
                        helperText={touched.status && errors.status}
                      >
                        {ENCOUNTER_STATUS.values.map((el) => (
                          <MenuItem key={el.code} value={el.code}>
                            {el.display}
                          </MenuItem>
                        ))}
                      </TextField>
                      <IconButton
                        aria-label="help"
                        onClick={() => {
                          handleFormHelpOpen(ENCOUNTER_STATUS);
                        }}
                      >
                        <HelpOutlineIcon />
                      </IconButton>
                    </Stack>

                    <Stack spacing={2}>
                      <Typography variant="subtitle1" sx={{ pt: 1 }}>
                        Period
                      </Typography>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <DateTimePicker
                        label="Start"
                        ampm={false}
                        timeSteps={{ hours: 1, minutes: 1 }}
                        value={values.period_start !== '' ? moment(values.period_start) : null}
                        onChange={(value) => {
                          if (value?.isValid) {
                            setFieldValue('period_start', value.format('YYYY-MM-DD[T]HH:mm:ssZ'));
                          }
                        }}
                      />
                      <DateTimePicker
                        label="End"
                        ampm={false}
                        timeSteps={{ hours: 1, minutes: 1 }}
                        value={values.period_end !== '' ? moment(values.period_end) : null}
                        onChange={(value) => {
                          if (value?.isValid) {
                            setFieldValue('period_end', value.format('YYYY-MM-DD[T]HH:mm:ssZ'));
                          }
                        }}
                      />
                    </Stack>

                    <Stack spacing={2}>
                      <Typography variant="subtitle1" sx={{ pt: 1 }}>
                        Patient
                      </Typography>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <AutoCompletePatient
                        setFieldValue={setFieldValue}
                        fieldName={'subject'}
                        values={values}
                        touched={touched}
                        errors={errors}
                        patientID={patientID}
                        label={'Subject'}
                        disabled={patientOptionsDisabled}
                      />
                    </Stack>

                    <Stack spacing={2}>
                      <Typography variant="subtitle1" sx={{ pt: 1 }}>
                        Location
                      </Typography>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <AutoCompleteLocation
                        setFieldValue={setFieldValue}
                        fieldName="location"
                        values={values}
                        touched={touched}
                        errors={errors}
                        locationID={isEdit ? values?.location : null}
                        label="Location (Part Of)"
                      />
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button color="error" onClick={handleClose}>
                Cancel
              </Button>
              <LoadingButton type="submit" loading={isSubmitting}>
                {isEdit ? 'Update' : 'Add'}
              </LoadingButton>
            </DialogActions>
          </Form>
        </FormikProvider>
      </Dialog>
      <FormHelpDialog isOpen={formHelpOpen} setIsOpen={setFormHelpOpen} valueSet={valueSet} />
    </>
  );
}
